import { Suspense, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { actorRole, useActor, useIsAdmin, useTitleRank } from '../lib/actor-store.ts';
import { api } from '../lib/api.ts';
import { useAuth } from '../lib/auth-store.ts';
import { StatusDictionaryLoader } from '../lib/status-dict.ts';
import {
  useCanViewBooks,
  useIsCashier,
  useIsFinance,
  useIsHr,
  useIsInvestment,
  useIsSysadminCap,
} from '../lib/use-capabilities.ts';
import { useIsMobile, useReadOnly } from '../lib/use-readonly.ts';
import { ActorSwitcher } from './ActorSwitcher.tsx';
import { EmployeeProfileDrawer } from './EmployeeProfileDrawer.tsx';
import { Toaster } from './Toaster.tsx';
import { WorkflowBell } from './WorkflowBell.tsx';

// Sidebar follows the prototype's group structure:
//   Dashboard (top, no group)
//   Phase 1 · 內部管理   — funds / projects / investors (active)
//   Phase 2 · LP Portal  — placeholders, disabled
//   Admin                — settings & members + master data
// Commitments are NOT a top-level page in the design; they live as
// sub-tabs inside fund / project / investor detail pages and the create
// modal is launched from those contexts. See PHASE1_DEPLOYED.md and the
// Phase 1 prototype at outputs/sanshi-handoff/prototype/index.html §sidebar.

interface NavItem {
  to: string;
  label: string;
  icon?: string;
  disabled?: boolean;
  adminOnly?: boolean;
  /**
   * 2026-07-05: adminOnly 預設是「member 看得到但灰掉」(Admin 群組的慣例)。
   * 放進「一般功能」群組的 adminOnly 項目 (如 收入) 設 adminHidden — 對非 admin
   * 完全不顯示 (不灰), 免得夾在人人可用的項目中間看起來像被鎖的功能。
   */
  adminHidden?: boolean;
  /** P1 (RFC §10): 只有系統管理員看得到（帳號/字典/工具/稽核）。非 sysadmin
   *  完全隱藏 — Partner 職級(業務 admin)也看不到。 */
  sysadminOnly?: boolean;
  /** 2026-07-12: 只有人資能力(hr) 或系統管理員看得到（HRM 管理功能）。對齊後端
   *  requireHrOrSysadmin；一般員工(含 Partner)完全隱藏。 */
  hrOnly?: boolean;
  /**
   * V318: 有「財務主管 / 出納」能力就看得到 (admin 也看得到)。
   * 用途跟 hrOnly 一樣 —— 能力應該決定看得到什麼, 而不是靠職級。
   * 之前財務全組是 adminOnly, 所以勾了財務主管在畫面上完全沒效果。
   */
  financeOnly?: boolean;
  /** V467: 有「看帳」資格 (管理員 / 財務主管 / 合夥人以上) 就看得到 —— 唯讀的財務報表。
   *  跟 financeOnly 一樣是**放行**條件; 看得到哪幾家記帳主體由指派決定 (後端 RLS)。 */
  booksViewOnly?: boolean;
  /**
   * V319: 有「投資主管」能力就看得到 (admin 也看得到)。
   * 投資者名單 (誰是我們的 LP) 對 PE firm 是敏感商業資訊, 不對一般成員開放。
   */
  investmentOnly?: boolean;
  /**
   * V320: 出納也看得到。 出納要回答的是「今天要付什麼、銀行帳對不對」,
   * 不是「公司賺多少、合夥人分多少 carry」 → 只掛在帳款 + 會計(內部只開銀行對帳分頁)。
   *
   * ⚠ 這是**對齊後端已經在做的事**, 不是新增限制:
   *   ap_can_access() (V245) 放行出納 → 帳款/銀行/預算/合約 進得去
   *   accounting.ts / dashboard carry 只認 user_is_finance → 出納本來就 403
   * 之前整組掛 financeOnly, 等於側邊欄對出納承諾了後端會拒絕的三個頁面。
   */
  cashierOnly?: boolean;
  /** V320: hrOnly 的項目中, 資訊主管也看得到的 (目前只有成員名冊)。 */
  sysadminAlso?: boolean;
}

interface NavGroup {
  heading?: string;
  items: ReadonlyArray<NavItem>;
}

// 2026-07-13 user spec — 依「模組」重排 sidebar,相關主檔/流程放同一分類:
//   - 一般功能 (員工日常, 全員可見): 儀表板 / 公告 / 簽核 / 專案 / 基金
//   - 投資者 / Investor (投資業務主檔, admin): 投資者 / 夥伴 / 使用者帳號
//   - 財務會計 / Finance (admin/會計): GP 公司(主體) / 財務報表 / 會計 / 帳款 / 收入
//   - 人資 / HRM (hr/系統管理員): 叁石成員 / 薪資
//   - 系統管理 / System (admin/系統管理員): 工作流程 / 修改紀錄 / 字典 / 開發工具
// 基金/專案 留「一般功能」(員工日常在用);收入歸「財務會計」(GP 管理費+carry)。
// 注意: sidebar 隱藏只是視覺乾淨,不取代 backend 權限 — detail page 仍走 RLS/V075
// manager-tier picker,一般 member 硬打網址也讀不到別人的資料。
/**
 * 側欄只顯示中文, 英文對照收進 hover。
 *
 * 2026-08-22 UX 稽核 #10: 21 個項目 + 5 個群組標題全部是「中文 / English」, 在
 * 240px 寬的側欄裡等於每一行有一半的寬度給了英文。 這是一個中文為主的團隊天天在看
 * 的東西, 英文是偶爾對照用的。
 *
 * 2026-09-14 使用者:「不需要再有 中文 / 英文 這樣的設計 就全部都是中文」→ hover 裡的
 *   英文也拿掉, label / heading 直接只存中文 (navZh 移除)。 查過: openGroups 只是
 *   記憶體內的 useState (沒有寫進 localStorage), React key 與「常用功能」判斷
 *   改名後照樣成立, 不牽動任何持久狀態。
 */
const NAV_GROUPS: ReadonlyArray<NavGroup> = [
  {
    heading: '常用功能',
    items: [
      { to: '/dashboard', label: '儀表板', icon: '📊' },
      // 公告全員可看(建立限總監以上,由後端 title_rank gate 管);簽核=全員報帳/請假/付款。
      { to: '/announcements', label: '公告', icon: '📢' },
      // 2026-08-22 UX 稽核 #1:「簽核」對新人讀起來是「核准別人的單」, 但全公司最常
      // 走這個入口的動作是**自己送報帳/請假**。 側欄沒有「報帳」也沒有「請假」,
      // 新人找不到 → 改成「申請與簽核」, 兩個方向都寫進去 (儀表板另有直達鈕)。
      { to: '/approvals', label: '申請與簽核', icon: '📝' },
      // 請假不進側欄 (2026-07-16 user: 請假送單走簽核, 側欄再放會跟簽核 + 人資的
      // 假勤管理重複)。個人假勤頁 (/leave, 餘額+紀錄) 維持在右上 UserMenu。
      // 先專案再基金(專案是主視角、基金是 wrapper);員工日常在用 → 留一般功能。
      // 案源 Pipeline 暫時隱藏 (2026-07-29 使用者要求拿掉)。
      // 後端 API + DB 不動, 要恢復把下面這行取消註解即可。
      // { to: '/pipeline', label: '案源', icon: '🔎' },
      { to: '/projects', label: '專案', icon: '🚀' },
      { to: '/funds', label: '基金', icon: '💼' },
    ],
  },
  {
    // 投資業務。 2026-07-20 使用者:「順序 要先建成 partner 才能是 investor 才能是
    // portal user 所以 nav 的順序要這樣改」——nav 照**建立流程**由上往下排:
    //   夥伴(主體檔) → 投資者(party + LP metadata) → 使用者帳號(投資人入口登入)
    // 一個對象要先是夥伴, 才能成為投資者, 有了投資者才會發投資人入口帳號。
    heading: '投資模組',
    items: [
      {
        // 2026-07-20 使用者拍板從「系統設定」移過來、且排**第一位**。
        // party 是底層主體檔 (投資者/廠商/GP 都指向它), 是投資關係的起點 ——
        // 一切從「建立夥伴」開始, 放這裡才看得出它是投資業務的第一步。
        to: '/parties',
        label: '叁石夥伴',
        icon: '🪪',
        adminOnly: true,
        adminHidden: true,
        investmentOnly: true,
      },
      {
        // 2026-07-19 我把這項對全員解開, 理由是「PII 遮罩 + member 在基金頁早就
        // 看得到 LP 名字, 隱藏純屬裝飾」。 2026-07-20 使用者指正, 那個推論有洞:
        //   · 在**自己負責的基金**頁看到該基金的 LP → 合理, 是工作範圍
        //   · 看到**全公司 33 個投資者**的名單 + 聯絡人 + email + AML 等級 → 另一件事
        // 對 PE firm 而言「我們的 LP 是誰」本身就是最敏感的商業資訊之一。
        // → 改回需要能力 (V319 投資主管), 而不是全開放, 也不是綁職級。
        to: '/investors',
        label: '投資者',
        icon: '👥',
        adminOnly: true,
        adminHidden: true,
        investmentOnly: true,
      },
      {
        to: '/lp-users',
        label: '使用者帳號',
        icon: '🪪',
        adminOnly: true,
        adminHidden: true,
        investmentOnly: true,
      },
    ],
  },
  {
    // 財務/會計: adminOnly + financeOnly → admin **或** 有財務/出納能力才看得到。
    // (2026-07-20 使用者:「為什麼 Amy 我已經勾選了財務主管 但是看不到財務模組」——
    //  原本全組 adminOnly, 而 admin 層要 ≥ 合夥人職級, 總監勾了能力也進不來。)
    // 收入(GP 管理費+carry 彙總)歸這裡,是敏感經濟數字;backend 仍 RLS-scoped。
    //
    // 2026-07-16 收斂 (7 項 → 4): 每一項回答一個問題, 不再是「一個 wave 一個 nav 項」。
    //   財務報表 = 我要看數字   (損益/資產負債 + 預算vs實際)
    //   會計     = 我要記帳/設定 (分錄 + 銀行對帳 + GP公司/科目/對應/鎖定)
    //   帳款     = 誰欠我/我欠誰 (AP + AR + 合約 + 廠商排行)
    //   收入     = GP 賺多少     (管理費 + carry + 服務費)
    // 併掉的理由: 財務報表原本錯位在「收入」底下 (它報整家 GP 含費用);預算跟損益表
    // 共用同一組 GP/年度/自結·審定 選單;AP/AR 是兩份鏡像 (同權限同心智模型);合約
    // 是「未來的應付」;銀行對帳是記帳的現金端;GP 公司是主檔 (本 app 慣例: 主檔住在
    // 用它的模組裡 — 廠商在 AP、客戶在 AR、銀行帳戶在銀行對帳)。
    // 舊路徑都在 App.tsx redirect 到對應分頁 (?tab=)。
    heading: '財務模組',
    items: [
      {
        to: '/reports',
        label: '財務報表',
        icon: '📊',
        adminOnly: true,
        adminHidden: true,
        financeOnly: true,
        // V467: 合夥人也看得到 (唯讀)。 頁面 gate 與後端 viewGuard 同一句, 三層一致。
        booksViewOnly: true,
      },
      // 2026-07-24 user spec: 順序 財務報表 → 收入 → 帳款 → 會計 → GP 設定。
      // 由粗到細 / 由結果到過程: 先看整體報表, 再看錢從哪來 (收入), 再看誰欠誰
      // (帳款), 再逐筆分錄 (會計); GP 設定是維護性質放最後。 日常看的擺前面。
      {
        to: '/revenue',
        label: '收入',
        icon: '💰',
        adminOnly: true,
        adminHidden: true,
        financeOnly: true,
      },
      {
        to: '/payables',
        label: '帳款',
        icon: '🧾',
        adminOnly: true,
        adminHidden: true,
        financeOnly: true,
        cashierOnly: true,
      },
      {
        to: '/accounting',
        label: '會計',
        icon: '📒',
        adminOnly: true,
        adminHidden: true,
        financeOnly: true,
        // 出納進得來, 但頁內只開「銀行對帳」分頁 (AccountingPage 自己篩)。
        // 對帳單 vs 實際付款的核對本來就是出納的工作。
        cashierOnly: true,
      },
      {
        // 2026-07-24 user spec: GP 設定移到本群**最下面** — 它是「設定/維護」性質
        // (GP 主體 + 銀行帳戶), 不是日常看的數字; 日常看的 (報表/收入/帳款/會計)
        // 擺前面, 要維護的擺後面。 (取代 2026-07-16「拉到第一位當錨」的擺法。)
        to: '/ledger-entities',
        label: '記帳主體',
        icon: '🏢',
        adminOnly: true,
        adminHidden: true,
        financeOnly: true,
      },
    ],
  },
  {
    // HRM = 人資管理功能,只有 hr 能力/系統管理員看得到(一般員工含 Partner 完全隱藏)。
    // 員工自助的假勤/個資在右上角個人選單,不放主 sidebar。
    heading: '人資模組',
    items: [
      // 成員名冊是**兩個能力共用**的一頁, 而且 V313 已經按欄位分權:
      //   人資主管  → 改雇用身分 (職稱/到職日/離職)
      //   資訊主管  → 改系統角色 + 能力勾選
      // 所以它是 hrOnly **或** sysadmin, 跟薪資/假勤不同 (那兩個純人事)。
      // 這也解掉一個雞生蛋: 若資訊主管進不了這頁, 就沒人能指派能力了。
      { to: '/settings', label: '叁石成員', icon: '⚙️', hrOnly: true, sysadminAlso: true },
      // 2026-07-20: 原本 hrOnly 的判斷是 `isHr || isSysadmin` —— 資訊主管**自動**
      // 看得到全員薪資、身分證、投保級距。 那是後門: 資訊主管是維運能力
      // (字典/log/工具), 跟人事資料沒關係。 改成只認人資主管。
      // ⚠ 影響: 只有 administrator 的人 (含 Samson) 會看不到薪資 → 要自己勾人資主管。
      //   這正是重點 —— 用明確的勾選取代隱含的後門, 查帳時看得出誰有權限看薪資。
      { to: '/payroll', label: '薪資', icon: '💰', hrOnly: true },
      { to: '/leave-admin', label: '假勤管理', icon: '🌴', hrOnly: true },
    ],
  },
  {
    // 系統維運 + 底層主檔。夥伴/工作流程 = admin;紀錄/字典/工具 = sysadmin only。
    heading: '系統設定',
    items: [
      // 2026-07-30: 從「叁石成員」的分頁搬過來 —— 簽核制度是全公司設定，
      // 不是人事資料（使用者指正）。跟字典 / 修改紀錄 同一層級。
      {
        to: '/approval-policies',
        label: '簽核路線',
        icon: '🧭',
        sysadminOnly: true,
      },
      { to: '/audit-log', label: '修改紀錄', icon: '🪵', sysadminOnly: true },
      { to: '/email-deliveries', label: '寄信紀錄', icon: '📬', sysadminOnly: true },
      { to: '/master-data', label: '字典', icon: '🗂️', sysadminOnly: true },
      // 清空所有資料(hard wipe) — dev-only、高破壞性,獨立擺最下面跟日常設定分開。
      { to: '/dev-tools', label: '開發工具', icon: '🛠️', sysadminOnly: true },
    ],
  },
];

/**
 * V395 —— 實習生 (職級 rank <= 10) 看得到的側邊欄。
 *
 * 判準是「他的工作需不需要」: 送單 (報帳/付款/請假/出差) + 看公告 + 自己的待辦。
 * 基金 / 專案留著, 但那兩張**清單後端已按指派收窄** —— 沒被指派看到的是空清單,
 * 被指派了就直接有入口。 藏掉反而要另外想辦法讓他進得去。
 *
 * ⚠ 這裡只管「看不看得到入口」; 真正的擋門在後端 (rejectMinimalSurface + 清單
 *   的 rank 述詞)。 只改這裡等於留一個按了會 403 的連結。
 */
/** 跟後端 api/helpers.ts 的 MINIMAL_SURFACE_MAX_RANK 同一個數字。 */
const MINIMAL_SURFACE_MAX_RANK = 10;

const MINIMAL_SURFACE_PATHS: ReadonlySet<string> = new Set([
  '/dashboard',
  '/announcements',
  '/approvals',
  '/projects',
  '/funds',
]);

export function Layout() {
  const isAdmin = useIsAdmin();
  // V323: 維運頁 (字典/稽核/開發工具) 的可見度改看**能力** (is_sysadmin) 而非
  // 登入 preset —— 否則非-Partner 拿到資訊主管能力的人 (actor='member') 會被
  // 當一般成員、看不到維運頁。 admin 是全系統超集, 另外用 isAdmin 一律放行。
  const isSysadmin = useIsSysadminCap();
  const isHr = useIsHr();
  // V395: 職級 rank —— 登入時由後端帶回 (LoginPage → setTitleRank)。
  const titleRank = useTitleRank();
  const isFinance = useIsFinance();
  const isInvestment = useIsInvestment();
  const isCashier = useIsCashier();
  const canViewBooks = useCanViewBooks();
  const isMobile = useIsMobile();
  const readOnly = useReadOnly();
  const [navOpen, setNavOpen] = useState(false);
  // 模組群組收合狀態(投資者/財務/人資/系統管理 預設收合;一般功能 常開)。
  // 當前所在頁的群組會自動展開,點標題可手動展開/收合其他組。
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (h: string) =>
    setOpenGroups((s) => {
      const n = new Set(s);
      if (n.has(h)) n.delete(h);
      else n.add(h);
      return n;
    });
  const location = useLocation();

  // Mobile: 切頁時自動關閉 hamburger drawer (避免遮住內容)。
  // biome-ignore lint/correctness/useExhaustiveDependencies: location.pathname is the trigger — effect body uses isMobile only, but we want re-fire on every navigation
  useEffect(() => {
    if (isMobile) setNavOpen(false);
  }, [location.pathname, isMobile]);

  // Mobile: 擋掉 ?edit=1 / /new 路徑 — 直接 navigate 回去 base
  // (純 URL hack 進來的 user 也擋掉 edit drawer 開啟)
  const navigate = useNavigate();
  useEffect(() => {
    if (!readOnly) return;
    const path = location.pathname;
    const newSuffixMatch = path.match(/^(\/[^/]+)\/new$/);
    if (newSuffixMatch) {
      navigate(newSuffixMatch[1] + location.search, { replace: true });
      return;
    }
    if (location.search.includes('edit=1')) {
      const next = new URLSearchParams(location.search);
      next.delete('edit');
      navigate(path + (next.toString() ? `?${next.toString()}` : ''), { replace: true });
    }
  }, [readOnly, location, navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* 狀態文字讀字典 (lib/status-dict.ts) —— 登入後才掛, 未登入打不了 API。 */}
      <StatusDictionaryLoader />
      {/* 2026-07-20 使用者:「scroll up 的時候 上面的 header 不要一起被 scroll up」
          → header 固定在頂端。 sidebar 也是 sticky top-0, 但它在 header 下方的 flex
          row 內、自己的 scroll context, 兩者不會疊 (見下方 sidebar top-0)。 */}
      <header className="sticky top-0 z-30 bg-slate-900 text-white px-4 sm:px-6 py-3 flex items-center justify-between shadow gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setNavOpen((v) => !v)}
            className="md:hidden text-2xl leading-none px-1"
            aria-label="開啟選單"
          >
            ☰
          </button>
          <div className="flex items-center gap-3 min-w-0">
            <img
              src="/tristone-on-dark.svg"
              alt="叁石資本"
              className="h-8 w-8 flex-shrink-0"
              draggable={false}
            />
            <div className="flex items-baseline gap-3 min-w-0">
              <span className="text-lg font-semibold whitespace-nowrap">叁石資本</span>
              <span className="hidden sm:inline text-xs text-slate-400 whitespace-nowrap">
                Tristone Capital · 內部系統
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <WorkflowBell />
          <UserMenu />
        </div>
      </header>

      {/* Mobile read-only banner — 提示 user 為何 +新增 / 編輯 都不見了。
          2026-09-04 使用者:「上面的 warning 看能不能一行簡短就好」—— 原文在
          375px 會擠成兩行, 縮短成一行(拿掉逐項列舉, 只留使用者真正要的結論;
          實測 375px 下單行寬 248px, 舒服塞進一行)。 */}
      {readOnly && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-1.5 text-[11px] text-amber-800 text-center">
          📱 手機僅可瀏覽 / 簽核 / 記案源，其餘請用電腦版
        </div>
      )}

      <div className="flex-1 flex relative">
        {/* Mobile drawer backdrop — click-to-dismiss; hamburger toggle 已是 keyboard 可控 */}
        {isMobile && navOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-30 md:hidden"
            onClick={() => setNavOpen(false)}
            aria-hidden
          />
        )}
        <nav
          className={`bg-white border-r border-slate-200 p-3 space-y-4 overflow-y-auto ${
            isMobile
              ? `fixed top-0 bottom-0 left-0 w-64 z-40 transform transition-transform duration-200 ${
                  navOpen ? 'translate-x-0' : '-translate-x-full'
                }`
              : 'w-60 sticky top-[60px] self-start max-h-[calc(100vh-60px)]'
          }`}
        >
          {NAV_GROUPS.map((group, gi) => {
            // 一個 item 對此使用者是否完全隱藏 (adminHidden / sysadminOnly / hrOnly 擋掉)。
            const hidden = (item: NavItem) =>
              // financeOnly: 有**財務主管**能力 → 即使不是 admin 也看得到。
              // (2026-08-25 useIsFinance 收窄成只含 finance —— 原本含出納, 等於
              //  cashierOnly 這個旗標從來沒生效過, 出納五項全看得到。)
              // 放最前面當短路 —— 它是「放行」條件, 要蓋過下面的 adminHidden。
              (item.financeOnly && isFinance) ||
              (item.booksViewOnly && canViewBooks) ||
              (item.cashierOnly && isCashier) ||
              (item.investmentOnly && isInvestment)
                ? false
                : (item.adminHidden && item.adminOnly && !isAdmin) ||
                  (item.sysadminOnly && !(isSysadmin || isAdmin)) ||
                  // V320: 人事資料只認人資主管; 成員名冊額外開給資訊主管 (見上)。
                  // V321 使用者:「admin 應該要包含全部的功能」→ 人事也算。
                  // 後端 requireHrOrSysadmin + employee_profile RLS 同步放行,
                  // 三層一致 (只改 nav 就是承諾一個後端會拒絕的頁面)。
                  (item.hrOnly && !(isHr || isAdmin || (item.sysadminAlso && isSysadmin))) ||
                  // V395 最小可用面 (2026-08-28 使用者:「實習生應該是看不到清單,
                  // 除非被 assign 進去」)。 rank <= 10 只留 申請與簽核 / 公告 /
                  // 儀表板 / 專案 / 基金 —— 後兩者留著是因為**清單本身已經按指派
                  // 收窄了**(後端 V395), 沒被指派就是空清單, 藏起來反而讓被指派
                  // 之後找不到入口。 其餘一律不出現。
                  // 後端 rejectMinimalSurface 同步 403, 兩層一致。
                  (titleRank <= MINIMAL_SURFACE_MAX_RANK && !MINIMAL_SURFACE_PATHS.has(item.to));
            const visibleItems = group.items.filter((item) => !hidden(item));
            // 整組都被隱藏 → 連 heading 都不要出現 (避免財務會計群對非 admin 留空標題)。
            if (visibleItems.length === 0) return null;
            // 常用功能(+無標題)常開;其餘模組群可收合。當前所在頁的群組自動展開。
            const collapsible = !!group.heading && group.heading !== '常用功能';
            const hasActive = visibleItems.some(
              (it) => location.pathname === it.to || location.pathname.startsWith(`${it.to}/`),
            );
            const open = !collapsible || openGroups.has(group.heading as string) || hasActive;
            return (
              <div key={group.heading ?? 'main'} className="space-y-0.5">
                {group.heading &&
                  (collapsible ? (
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.heading as string)}
                      className="w-full flex items-center justify-between px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-semibold text-slate-400 hover:text-slate-600"
                    >
                      <span>{group.heading}</span>
                      <span className="text-slate-300 text-[9px]">{open ? '▼' : '▶'}</span>
                    </button>
                  ) : (
                    <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                      {group.heading}
                    </div>
                  ))}
                {open &&
                  visibleItems.map((item) => {
                    const capBypass =
                      (item.financeOnly && isFinance) ||
                      (item.cashierOnly && isCashier) ||
                      (item.investmentOnly && isInvestment);
                    const greyed = item.disabled || (item.adminOnly && !isAdmin && !capBypass);
                    if (greyed) {
                      const title = item.disabled ? '尚未開放' : '僅管理員可使用';
                      return (
                        <div
                          key={item.to}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 cursor-not-allowed select-none"
                          title={`${item.label} · ${title}`}
                        >
                          {item.icon && <span className="opacity-60">{item.icon}</span>}
                          <span>{item.label}</span>
                        </div>
                      );
                    }
                    // 2026-07-07: 個人待辦 badge 移到右上角鈴鐺 (WorkflowBell), sidebar
                    // 不再掛 todo 數字。
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) =>
                          `flex items-center gap-2 px-3 py-2 text-sm rounded transition-colors ${
                            isActive
                              ? 'bg-slate-900 text-white'
                              : 'text-slate-700 hover:bg-slate-100'
                          }`
                        }
                      >
                        {item.icon && <span>{item.icon}</span>}
                        <span>{item.label}</span>
                      </NavLink>
                    );
                  })}
              </div>
            );
          })}
        </nav>
        <main className="flex-1 p-4 sm:p-6 overflow-auto min-w-0">
          <Suspense fallback={<PageSpinner />}>
            <Outlet />
          </Suspense>
        </main>
        <Toaster />
      </div>
    </div>
  );
}

function PageSpinner() {
  return (
    <div className="flex h-full items-center justify-center py-32">
      <div className="flex flex-col items-center gap-3">
        <svg
          className="h-7 w-7 animate-spin text-blue-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        <span className="text-sm text-slate-400">載入中…</span>
      </div>
    </div>
  );
}

// User dropdown — shows the logged-in name + role badge + logout. Phase 1
// keeps a tucked-away "Switch dev actor" link for QA against different
// actors without going through /login each time; once Entra OIDC lands
// that link goes away.
function UserMenu() {
  const navigate = useNavigate();
  const auth = useAuth();
  const actor = useActor((st) => st.actor);
  const role = auth.actor ? actorRole(auth.actor) : null;
  const [open, setOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const readOnly = useReadOnly();

  if (!auth.email) return null;

  const handleLogout = async () => {
    // 2026-09-08 安全掃描第三批：先告訴伺服器。
    // 只清 localStorage 的話 token 本身還活著到過期（最長 30 天）—— 共用電腦上
    // 按了登出、或懷疑 token 被複製走，都沒有辦法作廃。
    // best-effort：就算這一支失敗，本地 session 照清 —— 使用者按的是「登出」。
    await api('/api/v1/auth/logout', { actor, method: 'POST' }).catch(() => undefined);
    auth.clearSession();
    setOpen(false);
    // If MSAL is active, full logout via Entra redirect (clears MSAL cache +
    // optionally signs out at the IdP). Else fall back to local route nav.
    const msal = await import('../lib/msal.ts');
    if (msal.ENTRA_ENABLED && msal.getActiveAccount()) {
      await msal.logoutRedirect();
    } else {
      navigate('/login', { replace: true });
    }
  };

  const roleLabel = role === 'admin' ? '🔓 管理員' : role === 'lp' ? '👤 LP' : '🔒 一般成員';
  // V151: 「Samson (姚舜晏)」 form when name_zh present.
  const displayName = auth.name
    ? auth.nameZh
      ? `${auth.name} (${auth.nameZh})`
      : auth.name
    : (auth.email ?? '');

  return (
    <div className="relative text-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 rounded hover:bg-slate-800 transition-colors whitespace-nowrap"
        title={`${displayName} · ${roleLabel}`}
      >
        {/* 2026-07-22 mobile fix: 手機**完全隱藏**使用者名/email — 它最長 (247px 右組
            擠爆 header, 跟左邊標題重疊)。 手機只留 🔔 + 角色 emoji + ▾ (點開選單裡有全名);
            sm+ 才顯名字。 (原本 max-w-[120px] 截斷仍佔 120px, 不夠。) */}
        <span
          className="font-medium truncate max-w-[160px] hidden sm:inline"
          title={`${displayName} · ${roleLabel}`}
        >
          {displayName}
        </span>
        <span className="text-xs text-slate-400 hidden sm:inline">· {roleLabel}</span>
        <span className="text-xs hidden sm:inline">{/* full label on sm+ */}</span>
        <span className="text-xs sm:hidden" aria-label={roleLabel}>
          {role === 'admin' ? '🔓' : role === 'lp' ? '👤' : '🔒'}
        </span>
        <span className="text-slate-500">▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 mt-1 w-72 bg-white text-slate-700 rounded-lg shadow-xl border border-slate-200 z-40 overflow-hidden">
            <div className="p-3 border-b border-slate-100">
              <div className="font-medium text-slate-900">{displayName}</div>
              <div className="text-xs text-slate-500 font-mono mt-0.5">{auth.email}</div>
            </div>
            {/* Dev-actor switcher — QA-only impersonation via X-Sanshi-Dev-Actor
                header. 2026-07-02 (user spec 「應該不需要」): production 後端跑真
                驗證 (AUTH_ADAPTER=entra → LocalAuth JWT)，根本不收 dev header，這顆在
                線上是死的 leftover + 誤導 (看起來能切成 admin) + 洩漏內部帳號名。
                改成只在本機 dev build (import.meta.env.DEV) 顯示，正式站不出現。 */}
            {import.meta.env.DEV && !readOnly && (
              <div className="p-2">
                <details className="text-xs">
                  <summary className="cursor-pointer px-2 py-1.5 text-slate-500 hover:bg-slate-50 rounded select-none">
                    ⚙️ Switch dev actor (QA only)
                  </summary>
                  <div className="px-2 py-2">
                    <ActorSwitcher />
                  </div>
                </details>
              </div>
            )}
            {/* V243 → 2026-07-20: 「我的個資」改「我的帳戶」, 併入原本獨立的
                「修改密碼」(抽屜內分頁)。 個人選單原本五項且行為不一致 (個資/密碼
                開浮層、假勤/薪資單換頁)。 假勤與薪資單維持獨立入口 —— 使用者說
                兩個都常用, 收進分頁會從 1 點變 2 點。 */}
            {role === 'admin' || role === 'member' ? (
              <button
                type="button"
                onClick={() => {
                  setEditingProfile(true);
                  setOpen(false);
                }}
                className="w-full text-left px-4 py-2.5 text-sm border-t border-slate-100 hover:bg-slate-50 text-slate-700"
              >
                🪪 我的帳戶
              </button>
            ) : null}
            {/* 2026-07-12: 我的假勤 — 個人自助(看自己餘額 + 申請請假)。personal
                dashboard，放個人選單、不放主 sidebar。HR 管理面板在頁內以能力 gate。 */}
            {role === 'admin' || role === 'member' ? (
              <button
                type="button"
                onClick={() => {
                  navigate('/leave');
                  setOpen(false);
                }}
                className="w-full text-left px-4 py-2.5 text-sm border-t border-slate-100 hover:bg-slate-50 text-slate-700"
              >
                🌴 我的假勤
              </button>
            ) : null}
            {/* 2026-07-12: 我的薪資單 — 員工自助檢視/下載電子薪資單。個人自助。 */}
            {role === 'admin' || role === 'member' ? (
              <button
                type="button"
                onClick={() => {
                  navigate('/my-payslips');
                  setOpen(false);
                }}
                className="w-full text-left px-4 py-2.5 text-sm border-t border-slate-100 hover:bg-slate-50 text-slate-700"
              >
                💰 我的薪資單
              </button>
            ) : null}
            <button
              type="button"
              onClick={handleLogout}
              className="w-full text-left px-4 py-2.5 text-sm border-t border-slate-100 hover:bg-red-50 text-red-700"
            >
              登出
            </button>
          </div>
        </>
      )}
      {editingProfile && (
        <EmployeeProfileDrawer mode={{ kind: 'self' }} onClose={() => setEditingProfile(false)} />
      )}
    </div>
  );
}
